import { GetDefaults } from '@common/components/ComponentBase';
import { World } from '@common/World';

import { ClientRegistry } from './components';

export class ClientWorld extends World<ClientRegistry> {
  private serverToClientMap = new Map<number, number>();

  constructor(componentDefaults: GetDefaults<ClientRegistry>) {
    // Client doesn't broadcast payloads - disable tracking to avoid accumulating unused data
    super(componentDefaults, { trackingEnabled: false });
  }

  registerNetworkedEntity(serverEntityId: number): number {
    if (this.serverToClientMap.has(serverEntityId)) {
      return this.serverToClientMap.get(serverEntityId)!;
    }

    const clientEntityId = this.createEntity();
    this.serverToClientMap.set(serverEntityId, clientEntityId);

    const networkIdentity = this.createComponent('NetworkIdentity', {
      serverID: serverEntityId,
    });

    if (networkIdentity) {
      this.addComponent(clientEntityId, networkIdentity);
    }

    return clientEntityId;
  }

  markNetworkedEntityForDestruction(serverEntityId: number): void {
    if (this.serverToClientMap.has(serverEntityId)) {
      const clientEntityId = this.serverToClientMap.get(serverEntityId)!;

      // Mark all direct children for destruction first.
      // This ensures visual children (e.g., EquippedItemVisual sprites) are cleaned up
      // and don't persist with stale transforms after portal transitions.
      const children = this.getChildren(clientEntityId);
      for (const childId of children) {
        this.addComponent(childId, this.createComponent('MarkedForDestruction', {}));
      }

      this.addComponent(clientEntityId, this.createComponent('MarkedForDestruction', {}));
      this.serverToClientMap.delete(serverEntityId);
    }
  }

  getClientEntityId(serverEntityId: number): number | undefined {
    return this.serverToClientMap.get(serverEntityId);
  }

  removeNetworkedEntity(serverEntityId: number): void {
    const clientEntityId = this.serverToClientMap.get(serverEntityId);
    if (clientEntityId !== undefined) {
      this.destroyEntity(clientEntityId);
      this.serverToClientMap.delete(serverEntityId);
    }
  }

  resetWorld(): void {
    super.resetWorld();
    this.serverToClientMap.clear();
  }
}
